import fetch from "../../../../utils/fetch";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import {MERCURE_HUB_URL} from '../../../../config/_entrypoint';

const getInitialState = () => {
  return {
    abortController: null,
    isLoading: false,
    profileNotifications: [],
    navNotifications: [],
    error: null,
    lastNotification: null,
    lastNotificationId: null,
  }
}

const state = getInitialState();

const getters = {
  isLoading(state) {
    return state.isLoading
  },
  profileNotifications(state) {
    return state.profileNotifications
  },
  error(state) {
    return state.error
  },
  unreadCount(state) {
    let count = state.navNotifications.length
    return count >= 50 ? "50+" : count
  },
  lastTenNotifications(state) {
    return state.navNotifications.sort((a, b) => {return new Date(b.createdAt) - new Date(a.createdAt)}).slice(0, 10)
  },
  lastNotification(state) {
    return state.lastNotification
  },
}

const actions = {
  get({dispatch, commit}, data) {
    commit(types.SET_NOTIFICATIONS_LOADING)
    return fetch('/users/' + localStorage.getItem('userId') + '/notifications' + data.urlParams + '&isRead=false')
      .then((response) => {
        return response.json()
      })
      .then((data) => {
        commit(types.SET_PROFILE_NOTIFICATIONS, data['hydra:member'])
        return data
      })
      .catch((error) => {
        commit(types.SET_NOTIFICATIONS_ERROR, error)
      })
  },

  getUnread({dispatch, commit}) {
    if (!localStorage.getItem('userId')) return
    commit(types.SET_NOTIFICATIONS_LOADING)
    return fetch('/users/' + localStorage.getItem('userId') + '/notifications?page=1&itemsPerPage=50&order[createdAt]=desc&isRead=false')
      .then((response) => {
        return response.json()
      })
      .then((data) => {
        commit(types.SET_NAV_NOTIFICATIONS, data['hydra:member'])
        dispatch('setupMercure', null, {root: false})
      })
      .catch((error) => {
        commit(types.SET_NOTIFICATIONS_ERROR, error)
      })
  },

  setupMercure({state, commit, dispatch},) {
    // Mercure notifications setup - abort any existing connection
    if (state.abortController) {
      state.abortController.abort()
    }

    // If not logged in, skip mercure fetch
    if (!localStorage.getItem('userId')) return

    // Force a logout if the mercure token is missing
    const mercureToken = localStorage.getItem('mercureToken')
    if (!mercureToken) {
      dispatch("user/auth/triggerRouterLogout", null, {root: true})
      return
    }

    // Mercure connection URL with topic
    let url = new URL(MERCURE_HUB_URL)
    let topic = '/users/' + localStorage.getItem('userId') + '/notifications'
    url.searchParams.append('topic', topic)

    // Create abort controller for connection management
    state.abortController = new AbortController()

    // Try secure header-based auth first, fall back to query param if needed
    let useHeaderAuth = true

    const connectMercure = () => {
      if (useHeaderAuth) {
        // Secure method: Authorization header (not visible in logs/history)
        fetchEventSource(url.toString(), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${mercureToken}`,
          },
          signal: state.abortController.signal,

          async onopen(response) {
            if (response.ok) {
              console.log('mercure connection opened (header auth)')
            } else if (response.status === 401 || response.status === 403) {
              // Header auth failed, try legacy query param method
              console.log('mercure header auth failed, falling back to query param')
              useHeaderAuth = false
              connectMercure()
            } else {
              console.log('mercure connection closed with status:', response.status)
            }
          },

          onmessage(event) {
            commit(types.SET_NEW_NOTIFICATION, event)
          },

          onerror(err) {
            console.log('mercure connection error:', err)
          },

          openWhenHidden: false,
        }).catch((err) => {
          if (err.name !== 'AbortError') {
            console.log('mercure connection failed:', err)
            // Try auth as query param on error
            if (useHeaderAuth) {
              useHeaderAuth = false
              connectMercure()
            }
          }
        })
      } else {
        // Old method: with auth token as query parameter
        let oldUrl = new URL(url.toString())
        oldUrl.searchParams.append('authorization', mercureToken)

        const eventSource = new EventSource(oldUrl)
        eventSource.onopen = function() {
          console.log('mercure connection opened (query param)')
        }

        eventSource.onmessage = (event) => {
          commit(types.SET_NEW_NOTIFICATION, event)
        }

        eventSource.onerror = function() {
          console.log('mercure connection closed (query param)')
        }

        // Handle the connection termination
        const originalAbort = state.abortController.abort.bind(state.abortController)
        state.abortController.abort = () => {
          eventSource.close()
          originalAbort()
        }
      }
    }

    connectMercure()
  },

  close({state, commit}) {
    // Abort the Mercure connection before resetting state
    if (state.abortController) {
      state.abortController.abort()
    }
    commit(types.RESET)
  },

  markRead({commit}, id) {
    commit(types.SET_NOTIFICATIONS_LOADING)
    return fetch('/notifications/' + id, {
      method: 'PATCH',
      body: JSON.stringify({"isRead": true})
    }).then((response) => {
      return response.json()
    }).then((data) => {
      return data
    }).catch((error) => {
      commit(types.SET_NOTIFICATIONS_ERROR, error)
    })
  },

  markAllRead({commit}) {
    return fetch('/users/' + localStorage.getItem('userId') + '/notifications', {
      method: 'POST',
      body: JSON.stringify({"allRead": true})
    }).then((response) => {
      return response.status === 204
    }).then((data) => {
      return data
    }).catch((error) => {
      commit(types.SET_NOTIFICATIONS_ERROR, error)
    })
  },
}

const types = {
  SET_NOTIFICATIONS_LOADING: 'SET_NOTIFICATIONS_LOADING',
  SET_PROFILE_NOTIFICATIONS: 'SET_PROFILE_NOTIFICATIONS',
  SET_NAV_NOTIFICATIONS: 'SET_NAV_NOTIFICATIONS',
  SET_NOTIFICATIONS_ERROR: 'SET_NOTIFICATIONS_ERROR',

  SET_NEW_NOTIFICATION: 'SET_NEW_NOTIFICATION',
  RESET: 'RESET',
}

const mutations = {

  [types.SET_NOTIFICATIONS_LOADING](state) {
    state.isLoading = true;
  },

  [types.SET_PROFILE_NOTIFICATIONS](state, profileNotifications) {
    state.profileNotifications = profileNotifications;
    state.isLoading = false;
  },

  [types.SET_NAV_NOTIFICATIONS](state, navNotifications) {
    state.navNotifications = navNotifications
    state.isLoading = false;
  },

  [types.SET_NEW_NOTIFICATION](state, notification) {
    state.lastNotification = JSON.parse(notification.data)
    state.lastNotificationId = notification.lastEventId
    // Check if the incoming notification is marked read
    if (state.lastNotification.isRead) {
      state.navNotifications = state.navNotifications.filter(val => {
        return val.id !== state.lastNotification.id
      })
    } else {
      state.navNotifications.push(state.lastNotification)
    }
    state.isLoading = false;
  },

  [types.SET_NOTIFICATIONS_ERROR](state, error) {
    state.isLoading = true;
    state.error = error;
  },

  [types.RESET](state) {
    if (state.abortController) state.abortController.abort()
    Object.assign(state, getInitialState());
  },
}

export default {
  namespaced: true,
  state: state,
  getters: getters,
  actions: actions,
  mutations: mutations,
}
